import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { apiService } from "@/api/api";
import { useToast } from "@/hooks/use-toast";
import TherapeuticSidebar from "@/components/session/TherapeuticSidebar";
import SessionHeader from "@/components/session/SessionHeader";
import SessionNotesView from "@/components/session/SessionNotesView";
import SessionTranscriptView from "@/components/session/SessionTranscriptView";

interface SessionData {
  session_id?: string;
  original_dictation?: string;
  enhancedTranscriptContent?: {
      enhanced_transcript: Array<{
        turn_id: number;
        speaker_role: string;
        text: string;
      }>;
    };
  processedTranscriptContent?: string;
  // *** MODIFICATION END ***
  enhanced_sections?: {
    subjective?: string;
    objective?: string;
    assessment?: string;
    plan?: string;
    diagnosis?: string;
  };
  status?: string;
  template_type?: string;
  timestamp?: string;
  client_id?: string;
}

const Session = () => {
  const { session_id } = useParams<{ session_id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<SessionData>({});
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState("");
  const [sessionDate, setSessionDate] = useState("Session Date");
  const [activeView, setActiveView] = useState<'transcript' | 'notes' | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSessionAndClientData = async () => {
      if (!session_id) {
          toast({ title: "Error", description: "No session ID provided.", variant: "destructive" });
          navigate("/clients");
          return;
      }
      
      if (location.state?.clientName) {
        setClientName(location.state.clientName);
      }
      if (location.state?.sessionDate) {
        setSessionDate(location.state.sessionDate);
      }
      
      setLoading(true);
      try {
        const sessionApiResponse = await apiService.getSessionById(session_id);
        const sessionPayload = sessionApiResponse.data || sessionApiResponse;

        console.log("Session Payload:", sessionPayload);
        setSessionData(sessionPayload);
        
        if (sessionPayload.client_id) {
          try {
            const clientData = await apiService.getClient(sessionPayload.client_id);
            if (clientData) {
              setClientName(`${clientData.data.first_name} ${clientData.data.last_name}`);
            }
          } catch (clientError) {
             console.error("Could not fetch client details, using placeholder.", clientError);
          }
        }
        
        const dateString = sessionPayload.session_timestamp || sessionPayload.timestamp;
        if (dateString) {
          const date = new Date(dateString);
          setSessionDate(date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }));
        }

        setActiveView('notes');

      } catch (error) {
        console.error("Error fetching session data:", error);
        toast({
          title: "Error fetching session data",
          description: "Could not load the session data. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessionAndClientData();
  }, [session_id, navigate, toast, location.state]);

  const handleBackClick = () => {
    navigate("/clients");
  };

  const handleDelete = async () => {
    const confirmed = window.confirm("Are you sure you want to delete this session? This action cannot be undone.");
    
    if (confirmed) {
      if(!session_id) return;
      try {
        await apiService.deleteSession(session_id);
        toast({
          title: "Session Deleted",
          description: "The session has been successfully deleted.",
        });
        navigate("/clients");
      } catch (error) {
        toast({
          title: "Delete Failed",
          description: "Could not delete the session. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const handleSidebarAction = (action: 'transcript' | 'notes') => {
    setActiveView(action);
  };

  const getEnhancedSections = () => {
    console.log('enhanced_sections:', sessionData.enhanced_sections);
    return sessionData.enhanced_sections || {};
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="p-3 md:p-6 w-full">
          <Navbar />
          <main className="flex-1 overflow-y-auto mt-4 md:mt-6">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="flex flex-col items-center justify-center py-8 md:py-12">
                <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-psybytes-purple mb-4"></div>
                <p className="text-gray-500 text-sm md:text-base">Loading session data...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="p-3 md:p-6 w-full">
        <Navbar />
        <main className="flex-1 overflow-y-auto mt-4 md:mt-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <SessionHeader
              clientName={clientName}
              sessionDate={sessionDate}
              templateType={sessionData.template_type}
              status={sessionData.status}
              onBack={handleBackClick}
              onDelete={handleDelete}
            />

            <div className="flex flex-col lg:flex-row mt-4 md:mt-6 gap-4 md:gap-6 min-h-[calc(100vh-200px)] lg:min-h-screen">
              <div className="w-full lg:w-auto lg:flex-shrink-0">
                <TherapeuticSidebar 
                  onAction={handleSidebarAction} 
                  activeView={activeView}
                />
              </div>
              
              <div className="flex-1 space-y-4 md:space-y-6 overflow-y-auto px-2 sm:px-0">
                {activeView === 'transcript' && (
                  <SessionTranscriptView 
                    transcript={sessionData.original_dictation || sessionData.enhancedTranscriptContent?.enhanced_transcript || "No transcript or dictation available"}
                  />
                )}
                
                {activeView === 'notes' && (
                  <SessionNotesView sections={getEnhancedSections()} />
                )}
                
                {!activeView && (
                  <div className="flex items-center justify-center h-64 md:h-96">
                    <div className="text-center px-4">
                      <h3 className="text-lg md:text-xl font-semibold text-gray-600 mb-2">
                        Select an option from the sidebar
                      </h3>
                      <p className="text-gray-500 text-sm md:text-base">
                        Click on "Session Transcript" or "Session Notes" to view the content
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
      
    </div>
  );
};

export default Session;
